import type { IssueDependencyType, PMIssueDependency, PMIssue } from "@/domains/pm/types";
import { daysBetween, shiftISO } from "./ganttScale";

/**
 * Dependency arrows, and whether an edge is still satisfied.
 *
 * Only the CHECK lives here. The propagation — what actually moves when a date
 * changes — is `pm_compute_schedule_change` in Postgres and is deliberately
 * not duplicated: the chatbot has to produce the same schedule, and the client
 * only ever holds one page of issues. A per-edge predicate is different: it is
 * two dates and an integer, it is needed to paint an arrow red while the user
 * is looking at it, and it cannot disagree with the server about anything
 * except edges the page has not loaded.
 */

/** Which end of each bar an edge leaves from and arrives at. */
export interface EdgeSides {
  from: "start" | "end";
  to: "start" | "end";
}

export function sidesForType(type: IssueDependencyType): EdgeSides {
  switch (type) {
    case "FS":
      return { from: "end", to: "start" };
    case "SS":
      return { from: "start", to: "start" };
    case "FF":
      return { from: "end", to: "end" };
    case "SF":
      return { from: "start", to: "end" };
  }
}

/** Which edge pair a drag connected, i.e. what the user meant. */
export function typeForSides(from: "start" | "end", to: "start" | "end"): IssueDependencyType {
  if (from === "end" && to === "start") return "FS";
  if (from === "start" && to === "start") return "SS";
  if (from === "end" && to === "end") return "FF";
  return "SF";
}

export interface BarBox {
  left: number;
  width: number;
  top: number;
  height: number;
}

/** How far an arrow stands off a bar before turning. */
export const EDGE_STUB_PX = 10;

/**
 * An orthogonal path from one bar to another, as an SVG `d`.
 *
 * Right angles rather than curves: a Gantt is read by scanning a row and then
 * following a line down, and a bezier crossing three rows is ambiguous about
 * which one it lands on.
 */
export function edgePath(from: BarBox, to: BarBox, type: IssueDependencyType): string {
  const { from: fromSide, to: toSide } = sidesForType(type);

  const px = fromSide === "end" ? from.left + from.width : from.left;
  const py = from.top + from.height / 2;
  const qx = toSide === "start" ? to.left : to.left + to.width;
  const qy = to.top + to.height / 2;

  // Leaving a start edge goes left, leaving an end edge goes right. Arriving
  // at a start edge comes from the left, at an end edge from the right.
  const exit = fromSide === "end" ? 1 : -1;
  const entry = toSide === "start" ? 1 : -1;

  const p1x = px + exit * EDGE_STUB_PX;
  const q1x = qx - entry * EDGE_STUB_PX;

  // Is there room to go straight across, or does the arrow have to double back
  // between the two rows?
  const straight = entry > 0 ? q1x >= p1x : q1x <= p1x;

  const points: Array<[number, number]> = straight
    ? [
        [px, py],
        [p1x, py],
        [p1x, qy],
        [q1x, qy],
        [qx, qy],
      ]
    : [
        [px, py],
        [p1x, py],
        [p1x, midY(from, to)],
        [q1x, midY(from, to)],
        [q1x, qy],
        [qx, qy],
      ];

  return points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${round(x)} ${round(y)}`).join(" ");
}

/** The lane an arrow doubles back through: the gap between the two rows. */
function midY(from: BarBox, to: BarBox): number {
  const a = from.top + from.height / 2;
  const b = to.top + to.height / 2;
  return (a + b) / 2;
}

const round = (n: number) => Math.round(n * 10) / 10;

/** Where the arrowhead sits and which way it points, in degrees. */
export function arrowHead(
  to: BarBox,
  type: IssueDependencyType
): { x: number; y: number; rotate: number } {
  const { to: toSide } = sidesForType(type);
  const x = toSide === "start" ? to.left : to.left + to.width;
  return { x, y: to.top + to.height / 2, rotate: toSide === "start" ? 0 : 180 };
}

// ---------------------------------------------------------------------------
// Is the edge satisfied?
// ---------------------------------------------------------------------------

type Dates = Pick<PMIssue, "start_date" | "due_date">;

/** An issue's position on the axis: its start, or its due date when one-sided. */
export function anchorDate(issue: Dates): string | null {
  return issue.start_date ?? issue.due_date;
}

function durationDays(issue: Dates): number {
  if (!issue.start_date || !issue.due_date) return 0;
  return daysBetween(issue.start_date, issue.due_date);
}

/**
 * The earliest the successor may sit, expressed as its anchor date. Mirrors
 * pm_compute_schedule_change exactly — FF and SF drive the far end, so they
 * are converted to an implied start by subtracting the successor's duration.
 *
 * Null when the predecessor has no date the constraint can read, in which case
 * the edge constrains nothing.
 */
export function requiredAnchor(
  dep: Pick<PMIssueDependency, "dep_type" | "lag_days">,
  predecessor: Dates,
  successor: Dates
): string | null {
  const lag = dep.lag_days;
  const duration = durationDays(successor);
  switch (dep.dep_type) {
    case "FS":
      return predecessor.due_date ? shiftISO(predecessor.due_date, 1 + lag) : null;
    case "SS":
      return predecessor.start_date ? shiftISO(predecessor.start_date, lag) : null;
    case "FF":
      return predecessor.due_date ? shiftISO(predecessor.due_date, lag - duration) : null;
    case "SF":
      return predecessor.start_date ? shiftISO(predecessor.start_date, lag - duration) : null;
  }
}

/**
 * True when the successor sits EARLIER than the edge allows.
 *
 * Only earlier: a predecessor moving back leaves slack, which is a plan with
 * room in it, not a problem. Flagging that would paint half the chart red the
 * first time anything finished ahead of time.
 */
export function violatesConstraint(
  dep: Pick<PMIssueDependency, "dep_type" | "lag_days">,
  predecessor: Dates,
  successor: Dates
): boolean {
  const required = requiredAnchor(dep, predecessor, successor);
  const actual = anchorDate(successor);
  if (!required || !actual) return false;
  return actual < required;
}

// ---------------------------------------------------------------------------
// Drawing a new link
// ---------------------------------------------------------------------------

type EdgeEnds = Pick<PMIssueDependency, "predecessor_issue_id" | "successor_issue_id">;

/**
 * True when adding `from → to` would close a loop: `to` already reaches
 * `from` through the existing edges. The database refuses such an edge
 * (`pm_issue_dependency_cycle`), so the drag says so while the pointer is
 * still over the bar rather than after the release.
 */
export function wouldCreateCycle(edges: readonly EdgeEnds[], from: number, to: number): boolean {
  if (from === to) return true;
  const next = new Map<number, number[]>();
  for (const edge of edges) {
    const list = next.get(edge.predecessor_issue_id);
    if (list) list.push(edge.successor_issue_id);
    else next.set(edge.predecessor_issue_id, [edge.successor_issue_id]);
  }
  const seen = new Set<number>([to]);
  const stack = [to];
  for (let at = stack.pop(); at !== undefined; at = stack.pop()) {
    for (const successor of next.get(at) ?? []) {
      if (successor === from) return true;
      if (!seen.has(successor)) {
        seen.add(successor);
        stack.push(successor);
      }
    }
  }
  return false;
}

/** Why a link cannot be drawn between two issues. */
export type LinkRefusal = "self" | "duplicate" | "crossProject" | "cycle";

/**
 * The client's copy of the rules the dependency trigger enforces, checked in
 * the order a person would find most useful to hear: a pair that is already
 * linked is "already linked", not "a loop", even though it is both when the
 * existing edge runs the other way.
 */
export function linkRefusal(
  edges: readonly EdgeEnds[],
  from: Pick<PMIssue, "id" | "building_project_id">,
  to: Pick<PMIssue, "id" | "building_project_id">
): LinkRefusal | null {
  if (from.id === to.id) return "self";
  const linked = edges.some(
    (edge) =>
      (edge.predecessor_issue_id === from.id && edge.successor_issue_id === to.id) ||
      (edge.predecessor_issue_id === to.id && edge.successor_issue_id === from.id)
  );
  if (linked) return "duplicate";
  if (from.building_project_id !== to.building_project_id) return "crossProject";
  if (wouldCreateCycle(edges, from.id, to.id)) return "cycle";
  return null;
}

/** One end of the link being drawn, in the chart's coordinates. */
export interface LinkEnd {
  x: number;
  y: number;
  /** The bar end it is attached to; null for the free end under the cursor. */
  side: "start" | "end" | null;
}

/**
 * The curve drawn while a link is being dragged, as an SVG `d`.
 *
 * A curve here where the saved arrows are right-angled: this line follows the
 * hand, and a bezier leaving the bar in the direction its end faces reads as
 * "coming out of this end" at any angle, where an orthogonal path would flip
 * shape every time the cursor crossed the source's row.
 */
export function linkPreviewPath(from: LinkEnd, to: LinkEnd): string {
  const reach = Math.min(80, Math.max(24, Math.abs(to.x - from.x) / 2));
  const out = from.side === "start" ? -1 : 1;
  const c1x = from.x + out * reach;
  // An attached end is approached from outside the bar: a start from the
  // left, an end from the right. The free end is simply where the cursor is.
  const c2x = to.side === "start" ? to.x - reach : to.side === "end" ? to.x + reach : to.x;
  return `M${round(from.x)} ${round(from.y)} C${round(c1x)} ${round(from.y)} ${round(c2x)} ${round(to.y)} ${round(to.x)} ${round(to.y)}`;
}
